import { z } from 'zod';
import {
  browserSupportsWebAuthn,
  startAuthentication,
  startRegistration,
} from '@simplewebauthn/browser';
import {
  authResponseSchema,
  domainSchema,
  domainHealthSchema,
  awsPrepareResultSchema,
  gcpPrepareResultSchema,
  azurePrepareResultSchema,
  domainSyncResultSchema,
  domainSyncStatusSchema,
  reviewListSchema,
  reviewSettingsViewSchema,
  type ReviewList,
  type ReviewSettingsView,
  type UpdateReviewSettingsInput,
  knowledgeGraphSchema,
  knowledgeNodeDetailSchema,
  graphNeighborsSchema,
  graphSearchResultSchema,
  costAttributionSchema,
  costBreakdownSchema,
  bulkRecommendationResultSchema,
  estimateConfidenceSchema,
  orgSavingsSummarySchema,
  savingsReconciliationSchema,
  orgAssessmentSchema,
  feedbackReceiptSchema,
  type CreateFeedbackInput,
  type FeedbackReceipt,
  recommendationActivitySchema,
  recommendationCommentSchema,
  recommendationCommentsSchema,
  recommendationsSchema,
  recommendationSchema,
  recommendationHistorySchema,
  recommendationNarrationSchema,
  recommendationGuidanceSchema,
  recommendationMetricsSchema,
  complianceSummarySchema,
  complianceSettingsSchema,
  complianceEvidencePackSchema,
  complianceTrendSchema,
  complianceSnapshotSchema,
  orgCompliancePostureSchema,
  type ComplianceSummary,
  type ComplianceEvidencePack,
  type ComplianceTrend,
  type ComplianceSnapshot,
  type ComplianceSettings,
  type OrgCompliancePosture,
  type UpdateComplianceSettingsInput,
  costTrendsSchema,
  domainHistorySchema,
  resourceHistorySchema,
  resourceExplanationSchema,
  cachedExplanationSchema,
  domainAssessmentSchema,
  githubConnectResultSchema,
  gitlabConnectResultSchema,
  integrationHealthSchema,
  stateBackendTestResultSchema,
  awsKubernetesAccessTestResultSchema,
  integrationSchema,
  integrationValidationResultSchema,
  invitationPreviewSchema,
  invitationSchema,
  loginResultSchema,
  mfaStatusSchema,
  mfaEnrollStartSchema,
  mfaRecoveryCodesSchema,
  sandboxInviteReceiptSchema,
  registrationInfoSchema,
  betaInviteValidationSchema,
  webauthnOptionsSchema,
  memberSchema,
  notificationListSchema,
  onboardingStateSchema,
  organizationSchema,
  enabledFeaturesSchema,
  type EnabledFeatures,
  subscriptionUsageSchema,
  billingSummarySchema,
  syncRunSchema,
  userProfileSchema,
  versionInfoSchema,
  type AuthResponse,
  type AwsPrepareResult,
  type GcpPrepareResult,
  type GcpConnectRequest,
  type AzurePrepareResult,
  type AwsConnectRequest,
  type AzureConnectRequest,
  type GcpTestStateBackendRequest,
  type AwsTestStateBackendRequest,
  type AzureTestStateBackendRequest,
  type ConnectIntegrationInput,
  type CreateInvitationInput,
  type DomainSyncResult,
  type DomainSyncStatus,
  type GitHubConnectResult,
  type GitLabConnectResult,
  type KnowledgeGraph,
  type KnowledgeNodeDetail,
  type GraphNeighbors,
  type ResourceExplanation,
  type CachedExplanation,
  type BulkRecommendationInput,
  type BulkRecommendationResult,
  type DomainAssessment,
  type OrgSavingsSummary,
  type OrgAssessment,
  type RecommendationActivity,
  type RecommendationComment,
  type RecommendationComments,
  type GraphSearchResult,
  type CostAttribution,
  type CostBreakdown,
  type EstimateConfidence,
  type Recommendations,
  type Recommendation,
  type RecommendationMetrics,
  type RecommendationHistory,
  type RecommendationNarration,
  type RecommendationGuidance,
  type RecommendationPriorityTier,
  type UpdateRecommendationInput,
  type CostTrends,
  type DomainHistory,
  type ResourceHistory,
  type Integration,
  type StateBackend,
  type StateBackendTestResult,
  type AwsKubernetesAccessTestResult,
  type IntegrationHealth,
  type IntegrationValidationResult,
  type SyncRun,
  type Domain,
  type DomainHealth,
  type SavingsReconciliation,
  type DomainStatus,
  type Invitation,
  type InvitationPreview,
  type LoginInput,
  type LoginResult,
  type MfaPolicy,
  type MfaStatus,
  type MfaEnrollStart,
  type MfaRecoveryCodes,
  type Member,
  type NotificationList,
  type OnboardingState,
  type OnboardingStep,
  type Organization,
  type OrgRole,
  type RegisterInput,
  type RegistrationInfo,
  type BetaInviteValidation,
  type SandboxInviteReceipt,
  type StartSandboxInput,
  type SubscriptionUsage,
  type BillingSummary,
  type UpdateOrganizationInput,
  type UpdatePrivacySettingsInput,
  type UpdatePrivacySettingsResult,
  updatePrivacySettingsResultSchema,
  type EnableNameTokenizationResult,
  enableNameTokenizationResultSchema,
  type UpdateProfileInput,
  type UserProfile,
  type VersionInfo,
} from '@lizrd/contracts';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '';

/** Builds a versioned API URL. Centralized so callers never hardcode paths. */
export function apiUrl(path: string): string {
  const suffix = path.startsWith('/') ? path : `/${path}`;
  return `${API_BASE_URL}/api/v1${suffix}`;
}

// --- Access token (in memory only; refresh token lives in an httpOnly cookie) ---
let accessToken: string | null = null;
export function setAccessToken(token: string | null): void {
  accessToken = token;
}

async function toError(res: Response): Promise<Error> {
  try {
    const body = (await res.json()) as { error?: { message?: string } };
    return new Error(body?.error?.message ?? `Request failed (${res.status})`);
  } catch {
    return new Error(`Request failed (${res.status})`);
  }
}

async function postJson<T>(path: string, body: unknown, schema: z.ZodType<T>): Promise<T> {
  const res = await fetch(apiUrl(path), {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(body),
  });
  if (!res.ok) throw await toError(res);
  return schema.parse(await res.json());
}

/** Authenticated fetch: attaches the bearer token and, on 401, transparently
 * refreshes once before retrying. */
async function authFetch(path: string, init: RequestInit = {}, retry = true): Promise<Response> {
  const headers = new Headers(init.headers);
  if (accessToken) headers.set('Authorization', `Bearer ${accessToken}`);
  const res = await fetch(apiUrl(path), { ...init, headers, credentials: 'include' });
  if (res.status === 401 && retry && (await refreshSession())) {
    return authFetch(path, init, false);
  }
  return res;
}

// --- Auth ---
export async function register(input: RegisterInput): Promise<AuthResponse> {
  const result = await postJson('/auth/register', input, authResponseSchema);
  setAccessToken(result.accessToken);
  return result;
}

/** DEV ONLY (local testing harness): create a fresh throwaway org in the
 * ONBOARDING state and sign in — so the first-run wizard can be walked from the
 * top, repeatedly, with no email or re-register. Backed by a DEV_SEED_ENABLED-only
 * endpoint (404 in prod). */
export async function devStartOnboarding(): Promise<AuthResponse> {
  const result = await postJson('/dev/onboarding/start', {}, authResponseSchema);
  setAccessToken(result.accessToken);
  return result;
}

/** DEV ONLY: attach a fake connected cloud (+ seeded graph) to a domain so the
 * Connect step can be cleared without real cloud credentials. */
export async function devDemoConnect(domainId: string): Promise<{ integrationId: string }> {
  return postJson(
    '/dev/onboarding/demo-connect',
    { domainId },
    z.object({ integrationId: z.string() }),
  );
}

/** Public: is self-serve signup invite-only right now (private beta, Feature 27)? */
export async function getRegistrationInfo(): Promise<RegistrationInfo> {
  const res = await fetch(apiUrl('/auth/registration'), { credentials: 'include' });
  if (!res.ok) throw await toError(res);
  return registrationInfoSchema.parse(await res.json());
}

/** Public: does this emailed beta token unlock signup? (register-page gate). */
export function validateBetaInvite(token: string): Promise<BetaInviteValidation> {
  return postJson('/leads/beta-invite/validate', { token }, betaInviteValidationSchema);
}

/** Sign in. Returns either a full session (token set) or, for MFA-protected
 * accounts, a challenge the caller redeems via {@link verifyMfaLogin}. */
export async function login(input: LoginInput): Promise<LoginResult> {
  const result = await postJson('/auth/login', input, loginResultSchema);
  if ('accessToken' in result) setAccessToken(result.accessToken);
  return result;
}

/** Second step of a two-step login: redeem the challenge token + a code (TOTP
 * or recovery code) for a real session. */
export async function verifyMfaLogin(challengeToken: string, code: string): Promise<AuthResponse> {
  const result = await postJson('/auth/mfa/verify', { challengeToken, code }, authResponseSchema);
  setAccessToken(result.accessToken);
  return result;
}

/** Step 1 of the sandbox flow (Feature 24): verify the bot check and email a
 * single-use link. Does NOT sign anyone in — provisioning happens on verify. */
export function requestSandboxLink(input: StartSandboxInput): Promise<SandboxInviteReceipt> {
  return postJson('/public/sandbox/request', input, sandboxInviteReceiptSchema);
}

/** Step 2: redeem the emailed link token, provision the sandbox, and start a
 * session (sets the refresh cookie like login). */
export async function verifySandboxLink(token: string): Promise<AuthResponse> {
  const result = await postJson('/public/sandbox/verify', { token }, authResponseSchema);
  setAccessToken(result.accessToken);
  return result;
}

/** Re-seed the caller's sandbox back to its pristine start state. */
export async function resetSandbox(): Promise<void> {
  await authFetch('/sandbox/reset', { method: 'POST' });
}

// Single-flight guard for refresh. The refresh token rotates on every use, so if
// several requests 401 at once and each POSTs /auth/refresh independently, the first
// rotates the cookie and the rest send the now-invalidated token → they fail and null
// the session, logging the user out mid-session. Coalesce concurrent refreshes onto
// one in-flight request so everyone shares its result.
let inFlightRefresh: Promise<AuthResponse | null> | null = null;

/** Restore a session from the refresh cookie. Returns null when unauthenticated.
 * Concurrent calls share a single in-flight refresh (see {@link inFlightRefresh}). */
export function refreshSession(): Promise<AuthResponse | null> {
  if (inFlightRefresh) return inFlightRefresh;
  inFlightRefresh = doRefreshSession().finally(() => {
    inFlightRefresh = null;
  });
  return inFlightRefresh;
}

async function doRefreshSession(): Promise<AuthResponse | null> {
  const res = await fetch(apiUrl('/auth/refresh'), { method: 'POST', credentials: 'include' });
  if (!res.ok) {
    setAccessToken(null);
    return null;
  }
  const result = authResponseSchema.parse(await res.json());
  setAccessToken(result.accessToken);
  return result;
}

export async function logout(): Promise<void> {
  await fetch(apiUrl('/auth/logout'), { method: 'POST', credentials: 'include' });
  setAccessToken(null);
}

/** Sign out of every session on all devices (AUTH-008). */
export async function logoutAll(): Promise<void> {
  await authFetch('/auth/logout-all', { method: 'POST' });
  setAccessToken(null);
}

export async function fetchMe(): Promise<UserProfile> {
  const res = await authFetch('/auth/me');
  if (!res.ok) throw await toError(res);
  return userProfileSchema.parse(await res.json());
}

export async function verifyEmail(token: string): Promise<void> {
  const res = await fetch(apiUrl('/auth/verify-email'), {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ token }),
  });
  if (!res.ok) throw await toError(res);
}

export async function resendVerification(): Promise<void> {
  const res = await authFetch('/auth/resend-verification', { method: 'POST' });
  if (!res.ok) throw await toError(res);
}

/** Always resolves (server never reveals whether the email exists). */
export async function forgotPassword(email: string): Promise<void> {
  await fetch(apiUrl('/auth/forgot-password'), {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ email }),
  });
}

export async function resetPassword(token: string, password: string): Promise<void> {
  const res = await fetch(apiUrl('/auth/reset-password'), {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ token, password }),
  });
  if (!res.ok) throw await toError(res);
}

// --- Authenticated JSON helpers ---
async function authJson<T>(path: string, schema: z.ZodType<T>, init: RequestInit = {}): Promise<T> {
  const res = await authFetch(path, init);
  if (!res.ok) throw await toError(res);
  return schema.parse(await res.json());
}

function jsonInit(method: string, body?: unknown): RequestInit {
  return {
    method,
    headers: { 'content-type': 'application/json' },
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  };
}

// --- Domains ---
export function listDomains(): Promise<Domain[]> {
  return authJson('/domains', z.array(domainSchema));
}
export function getDomain(id: string): Promise<Domain> {
  return authJson(`/domains/${id}`, domainSchema);
}
export function createDomain(name: string): Promise<Domain> {
  return authJson('/domains', domainSchema, jsonInit('POST', { name }));
}

// --- Onboarding ---
/** Advance guided onboarding past the step the client just finished. */
export function advanceOnboarding(from: OnboardingStep): Promise<OnboardingState> {
  return authJson(
    '/organization/onboarding/advance',
    onboardingStateSchema,
    jsonInit('POST', { from }),
  );
}
/** Step guided onboarding back to the previous visible step. */
export function goBackOnboarding(from: OnboardingStep): Promise<OnboardingState> {
  return authJson(
    '/organization/onboarding/back',
    onboardingStateSchema,
    jsonInit('POST', { from }),
  );
}
export function updateDomain(
  id: string,
  patch: { name?: string; showLeaderboard?: boolean },
): Promise<Domain> {
  return authJson(`/domains/${id}`, domainSchema, jsonInit('PATCH', patch));
}
export function setDomainStatus(id: string, status: DomainStatus): Promise<Domain> {
  return authJson(`/domains/${id}/status`, domainSchema, jsonInit('POST', { status }));
}
export async function grantDomainAccess(id: string, userId: string): Promise<void> {
  const res = await authFetch(`/domains/${id}/members`, jsonInit('POST', { userId }));
  if (!res.ok) throw await toError(res);
}
export async function revokeDomainAccess(id: string, userId: string): Promise<void> {
  const res = await authFetch(`/domains/${id}/members/${userId}`, { method: 'DELETE' });
  if (!res.ok) throw await toError(res);
}

// --- Reviews (CI/CD shift-left) ---
export function getReviews(domainId: string): Promise<ReviewList> {
  return authJson(`/domains/${domainId}/reviews`, reviewListSchema);
}
export function getReviewSettings(domainId: string): Promise<ReviewSettingsView> {
  return authJson(`/domains/${domainId}/reviews/settings`, reviewSettingsViewSchema);
}
export function updateReviewSettings(
  domainId: string,
  patch: UpdateReviewSettingsInput,
): Promise<ReviewSettingsView> {
  return authJson(
    `/domains/${domainId}/reviews/settings`,
    reviewSettingsViewSchema,
    jsonInit('PATCH', patch),
  );
}
/** Dev-only: enqueue a fixture PR through the review pipeline (gated by DEV_SEED_ENABLED). */
export function simulateReview(
  domainId: string,
): Promise<{ repoFullName: string; prNumber: number; provider: string }> {
  return authJson(
    `/domains/${domainId}/reviews/simulate`,
    z.object({ repoFullName: z.string(), prNumber: z.number(), provider: z.string() }),
    jsonInit('POST'),
  );
}

// --- Members ---
export function listMembers(): Promise<Member[]> {
  return authJson('/members', z.array(memberSchema));
}
export async function changeMemberRole(userId: string, role: OrgRole): Promise<void> {
  const res = await authFetch(`/members/${userId}/role`, jsonInit('PATCH', { role }));
  if (!res.ok) throw await toError(res);
}
export async function removeMember(userId: string): Promise<void> {
  const res = await authFetch(`/members/${userId}`, { method: 'DELETE' });
  if (!res.ok) throw await toError(res);
}
/** Admin recovery: clear a locked-out member's MFA (Feature 25, Phase D). */
export async function resetMemberMfa(userId: string): Promise<void> {
  const res = await authFetch(`/members/${userId}/mfa/reset`, { method: 'POST' });
  if (!res.ok) throw await toError(res);
}
/** Replace the full set of domains an Engineer/Viewer can access (RBAC-002). */
export async function setMemberDomains(userId: string, domainIds: string[]): Promise<void> {
  const res = await authFetch(`/members/${userId}/domains`, jsonInit('PUT', { domainIds }));
  if (!res.ok) throw await toError(res);
}
export function updateProfile(input: UpdateProfileInput): Promise<UserProfile> {
  return authJson('/auth/me', userProfileSchema, jsonInit('PATCH', input));
}

/** Send yourself a preview of the weekly Optimization Digest (Feature 26). */
export function sendWeeklyDigestPreview(): Promise<{ sent: boolean }> {
  return authJson('/me/weekly-digest/preview', z.object({ sent: z.boolean() }), jsonInit('POST'));
}

// --- Multi-factor authentication (Feature 25) ---
export function getMfaStatus(): Promise<MfaStatus> {
  return authJson('/auth/mfa/status', mfaStatusSchema);
}
/** Begin TOTP enrollment: returns a QR + manual secret for the authenticator app. */
export function startMfaEnrollment(): Promise<MfaEnrollStart> {
  return authJson('/auth/mfa/totp/enroll', mfaEnrollStartSchema, jsonInit('POST'));
}
/** Confirm enrollment with a code; returns the one-time recovery codes (shown once). */
export function activateMfaEnrollment(factorId: string, code: string): Promise<MfaRecoveryCodes> {
  return authJson(
    '/auth/mfa/totp/activate',
    mfaRecoveryCodesSchema,
    jsonInit('POST', { factorId, code }),
  );
}
export function regenerateRecoveryCodes(): Promise<MfaRecoveryCodes> {
  return authJson('/auth/mfa/recovery-codes/regenerate', mfaRecoveryCodesSchema, jsonInit('POST'));
}
export async function removeMfaFactor(id: string): Promise<void> {
  const res = await authFetch(`/auth/mfa/factors/${id}`, { method: 'DELETE' });
  if (!res.ok) throw await toError(res);
}

// --- WebAuthn / passkeys (Feature 25, Phase C) ---
/** Whether this browser can do WebAuthn at all (gates the passkey UI). */
export function passkeysSupported(): boolean {
  return browserSupportsWebAuthn();
}

/** Register a passkey: fetch creation options, run the browser ceremony, verify.
 * Returns recovery codes only when this is the user's first factor. */
export async function addPasskey(label?: string): Promise<MfaRecoveryCodes> {
  const options = await authJson(
    '/auth/mfa/webauthn/register/options',
    webauthnOptionsSchema,
    jsonInit('POST'),
  );
  const response = await startRegistration(
    options as unknown as Parameters<typeof startRegistration>[0],
  );
  return authJson(
    '/auth/mfa/webauthn/register/verify',
    mfaRecoveryCodesSchema,
    jsonInit('POST', { response, label }),
  );
}

/** Second step of login with a passkey: assertion options → browser ceremony →
 * verify for a real session. */
export async function passkeyLogin(challengeToken: string): Promise<AuthResponse> {
  const options = await postJson(
    '/auth/mfa/webauthn/login/options',
    { challengeToken },
    webauthnOptionsSchema,
  );
  const response = await startAuthentication(
    options as unknown as Parameters<typeof startAuthentication>[0],
  );
  const result = await postJson(
    '/auth/mfa/webauthn/login/verify',
    { challengeToken, response },
    authResponseSchema,
  );
  setAccessToken(result.accessToken);
  return result;
}

// --- Invitations ---
export function listInvitations(): Promise<Invitation[]> {
  return authJson('/invitations', z.array(invitationSchema));
}
/** Public: resolve an invite token to who/what it's for (for the accept page). */
export async function getInvitationPreview(token: string): Promise<InvitationPreview> {
  const res = await fetch(apiUrl(`/invitations/by-token?token=${encodeURIComponent(token)}`));
  if (!res.ok) throw await toError(res);
  return invitationPreviewSchema.parse(await res.json());
}
export function createInvitation(input: CreateInvitationInput): Promise<Invitation> {
  return authJson('/invitations', invitationSchema, jsonInit('POST', input));
}
export async function revokeInvitation(id: string): Promise<void> {
  const res = await authFetch(`/invitations/${id}`, { method: 'DELETE' });
  if (!res.ok) throw await toError(res);
}
/** Accept an invitation and sign in as the new user (session replaces any prior one). */
export async function acceptInvitation(
  token: string,
  name: string,
  password: string,
): Promise<AuthResponse> {
  const result = await postJson(
    '/invitations/accept',
    { token, name, password },
    authResponseSchema,
  );
  setAccessToken(result.accessToken);
  return result;
}

// --- Subscription ---
export function getSubscriptionUsage(): Promise<SubscriptionUsage> {
  return authJson('/subscription', subscriptionUsageSchema);
}

// --- Billing (admin-only) ---
export function getBillingSummary(): Promise<BillingSummary> {
  return authJson('/billing/summary', billingSummarySchema);
}

/** Trigger an on-demand billed-cost refresh for the current month, returning the
 * freshly-recomputed summary. */
export function refreshBilling(): Promise<BillingSummary> {
  return authJson('/billing/refresh', billingSummarySchema, { method: 'POST' });
}

/** Start a Stripe Checkout session to save a card; returns the hosted URL to
 * redirect the browser to (Phase 2c). */
export function startStripeCheckout(): Promise<{ url: string }> {
  return authJson('/billing/stripe/checkout-session', z.object({ url: z.string().url() }), {
    method: 'POST',
  });
}

/** Staging-only: run a full charge cycle (invoice + statement email + Stripe charge)
 * and return the refreshed summary. 404s in production. */
export function simulateCharge(): Promise<BillingSummary> {
  return authJson('/billing/simulate-charge', billingSummarySchema, { method: 'POST' });
}

// --- Integrations (env-scoped) ---
const integrationsBase = (envId: string) => `/domains/${envId}/integrations`;

export function listIntegrations(envId: string): Promise<Integration[]> {
  return authJson(integrationsBase(envId), z.array(integrationSchema));
}
export function connectIntegration(
  envId: string,
  input: ConnectIntegrationInput,
): Promise<Integration> {
  return authJson(integrationsBase(envId), integrationSchema, jsonInit('POST', input));
}
/** Connect GitHub by repository; may return an install URL if the App isn't installed. */
export function connectGitHub(
  envId: string,
  input: { repository: string; displayName?: string },
): Promise<GitHubConnectResult> {
  return authJson(
    `${integrationsBase(envId)}/github/connect`,
    githubConnectResultSchema,
    jsonInit('POST', input),
  );
}

export function connectGitLab(
  envId: string,
  input: { token: string; baseUrl: string; groupPath?: string; displayName?: string },
): Promise<GitLabConnectResult> {
  return authJson(
    `${integrationsBase(envId)}/gitlab/connect`,
    gitlabConnectResultSchema,
    jsonInit('POST', input),
  );
}
/** Begin AWS setup: get an ExternalId + a CloudFormation quick-create link. */
export function prepareAws(envId: string): Promise<AwsPrepareResult> {
  return authJson(
    `${integrationsBase(envId)}/aws/prepare`,
    awsPrepareResultSchema,
    jsonInit('POST'),
  );
}
/** Complete AWS connect once the customer created the role. */
export function connectAws(envId: string, input: AwsConnectRequest): Promise<Integration> {
  return authJson(
    `${integrationsBase(envId)}/aws/connect`,
    integrationSchema,
    jsonInit('POST', input),
  );
}
export function prepareGcp(envId: string): Promise<GcpPrepareResult> {
  return authJson(
    `${integrationsBase(envId)}/gcp/prepare`,
    gcpPrepareResultSchema,
    jsonInit('POST'),
  );
}
export function connectGcp(envId: string, input: GcpConnectRequest): Promise<Integration> {
  return authJson(
    `${integrationsBase(envId)}/gcp/connect`,
    integrationSchema,
    jsonInit('POST', input),
  );
}
export function prepareAzure(envId: string): Promise<AzurePrepareResult> {
  return authJson(
    `${integrationsBase(envId)}/azure/prepare`,
    azurePrepareResultSchema,
    jsonInit('POST'),
  );
}
export function connectAzure(envId: string, input: AzureConnectRequest): Promise<Integration> {
  return authJson(
    `${integrationsBase(envId)}/azure/connect`,
    integrationSchema,
    jsonInit('POST', input),
  );
}
export function testGcpStateBackend(
  envId: string,
  input: GcpTestStateBackendRequest,
): Promise<StateBackendTestResult> {
  return authJson(
    `${integrationsBase(envId)}/gcp/test-state-backend`,
    stateBackendTestResultSchema,
    jsonInit('POST', input),
  );
}
export function testAwsStateBackend(
  envId: string,
  input: AwsTestStateBackendRequest,
): Promise<StateBackendTestResult> {
  return authJson(
    `${integrationsBase(envId)}/aws/test-state-backend`,
    stateBackendTestResultSchema,
    jsonInit('POST', input),
  );
}
export function testAzureStateBackend(
  envId: string,
  input: AzureTestStateBackendRequest,
): Promise<StateBackendTestResult> {
  return authJson(
    `${integrationsBase(envId)}/azure/test-state-backend`,
    stateBackendTestResultSchema,
    jsonInit('POST', input),
  );
}
export function validateIntegration(
  envId: string,
  id: string,
): Promise<IntegrationValidationResult> {
  return authJson(
    `${integrationsBase(envId)}/${id}/validate`,
    integrationValidationResultSchema,
    jsonInit('POST'),
  );
}
export function triggerIntegrationSync(envId: string, id: string): Promise<SyncRun> {
  return authJson(`${integrationsBase(envId)}/${id}/sync`, syncRunSchema, jsonInit('POST'));
}
export function getIntegrationHealth(envId: string, id: string): Promise<IntegrationHealth> {
  return authJson(`${integrationsBase(envId)}/${id}/health`, integrationHealthSchema);
}
export async function disconnectIntegration(envId: string, id: string): Promise<void> {
  const res = await authFetch(`${integrationsBase(envId)}/${id}`, { method: 'DELETE' });
  if (!res.ok) throw await toError(res);
}
export function setStateBackend(
  envId: string,
  id: string,
  stateBackend: StateBackend | null,
): Promise<Integration> {
  return authJson(
    `${integrationsBase(envId)}/${id}/state-backend`,
    integrationSchema,
    jsonInit('PATCH', { stateBackend }),
  );
}
export function testStateBackend(
  envId: string,
  id: string,
  stateBackend: StateBackend,
): Promise<StateBackendTestResult> {
  return authJson(
    `${integrationsBase(envId)}/${id}/state-backend/test`,
    stateBackendTestResultSchema,
    jsonInit('POST', { stateBackend }),
  );
}
export function setKubernetesAccess(
  envId: string,
  id: string,
  kubernetesAccess: { enabled: boolean; autoProvision?: boolean } | null,
): Promise<Integration> {
  return authJson(
    `${integrationsBase(envId)}/${id}/kubernetes-access`,
    integrationSchema,
    jsonInit('PATCH', { kubernetesAccess }),
  );
}
export function testKubernetesAccess(
  envId: string,
  id: string,
): Promise<AwsKubernetesAccessTestResult> {
  return authJson(
    `${integrationsBase(envId)}/${id}/kubernetes-access/test`,
    awsKubernetesAccessTestResultSchema,
    jsonInit('POST', {}),
  );
}

// --- Customer Knowledge Graph (Explorer) ---
function graphBase(envId: string): string {
  return `/domains/${envId}/graph`;
}
export function getGraph(envId: string, type?: string): Promise<KnowledgeGraph> {
  const q = type ? `?type=${encodeURIComponent(type)}` : '';
  return authJson(`${graphBase(envId)}${q}`, knowledgeGraphSchema);
}
export function searchGraph(
  envId: string,
  query: string,
  type?: string,
): Promise<GraphSearchResult> {
  const params = new URLSearchParams({ q: query });
  if (type) params.set('type', type);
  return authJson(`${graphBase(envId)}/search?${params.toString()}`, graphSearchResultSchema);
}
export function getGraphNode(envId: string, nodeId: string): Promise<KnowledgeNodeDetail> {
  return authJson(`${graphBase(envId)}/nodes/${nodeId}`, knowledgeNodeDetailSchema);
}
export function getGraphNeighbors(envId: string, nodeId: string): Promise<GraphNeighbors> {
  return authJson(`${graphBase(envId)}/nodes/${nodeId}/neighbors`, graphNeighborsSchema);
}
export function getGraphCost(envId: string): Promise<CostAttribution> {
  return authJson(`${graphBase(envId)}/cost`, costAttributionSchema);
}
export function getCostBreakdown(envId: string): Promise<CostBreakdown> {
  return authJson(`${graphBase(envId)}/cost-breakdown`, costBreakdownSchema);
}
export function getEstimateConfidence(envId: string): Promise<EstimateConfidence> {
  return authJson(`${graphBase(envId)}/estimate-confidence`, estimateConfidenceSchema);
}
export function getRecommendations(envId: string): Promise<Recommendations> {
  return authJson(`${graphBase(envId)}/recommendations`, recommendationsSchema);
}

// --- NIS2 Compliance ---
function complianceBase(domainId: string): string {
  return `/domains/${domainId}/compliance`;
}
export function getComplianceSummary(domainId: string): Promise<ComplianceSummary> {
  return authJson(complianceBase(domainId), complianceSummarySchema);
}
export function getComplianceEvidence(domainId: string): Promise<ComplianceEvidencePack> {
  return authJson(`${complianceBase(domainId)}/evidence`, complianceEvidencePackSchema);
}
export function getComplianceTrend(domainId: string): Promise<ComplianceTrend> {
  return authJson(`${complianceBase(domainId)}/trend`, complianceTrendSchema);
}
export function listComplianceSnapshots(domainId: string): Promise<ComplianceSnapshot[]> {
  return authJson(`${complianceBase(domainId)}/snapshots`, z.array(complianceSnapshotSchema));
}
export function captureComplianceSnapshot(domainId: string): Promise<ComplianceSnapshot | null> {
  return authJson(
    `${complianceBase(domainId)}/snapshots`,
    complianceSnapshotSchema.nullable(),
    jsonInit('POST'),
  );
}
export function getComplianceSettings(domainId: string): Promise<ComplianceSettings> {
  return authJson(`${complianceBase(domainId)}/settings`, complianceSettingsSchema);
}
export function updateComplianceSettings(
  domainId: string,
  input: UpdateComplianceSettingsInput,
): Promise<ComplianceSettings> {
  return authJson(
    `${complianceBase(domainId)}/settings`,
    complianceSettingsSchema,
    jsonInit('PUT', input),
  );
}
export function getOrgCompliancePosture(): Promise<OrgCompliancePosture> {
  return authJson('/compliance/posture', orgCompliancePostureSchema);
}
export function generateComplianceFix(
  domainId: string,
  gapId: string,
): Promise<RecommendationGuidance> {
  return authJson(
    `${complianceBase(domainId)}/gaps/${encodeURIComponent(gapId)}/fix`,
    recommendationGuidanceSchema,
    jsonInit('POST'),
  );
}
export function getRecommendationHistory(
  envId: string,
  id: string,
): Promise<RecommendationHistory> {
  return authJson(`${graphBase(envId)}/recommendations/${id}/history`, recommendationHistorySchema);
}
export function updateRecommendation(
  envId: string,
  id: string,
  input: UpdateRecommendationInput,
): Promise<Recommendation> {
  return authJson(
    `${graphBase(envId)}/recommendations/${id}`,
    recommendationSchema,
    jsonInit('PATCH', input),
  );
}
export function narrateRecommendation(envId: string, id: string): Promise<RecommendationNarration> {
  return authJson(
    `${graphBase(envId)}/recommendations/${id}/narrate`,
    recommendationNarrationSchema,
    jsonInit('POST'),
  );
}
export function getRecommendationGuidance(
  envId: string,
  id: string,
): Promise<RecommendationGuidance | null> {
  return authJson(
    `${graphBase(envId)}/recommendations/${id}/guidance`,
    recommendationGuidanceSchema.nullable(),
  );
}
export function setRecommendationPriority(
  envId: string,
  id: string,
  userPriorityTier: RecommendationPriorityTier | null,
): Promise<Recommendation> {
  return authJson(
    `${graphBase(envId)}/recommendations/${id}/priority`,
    recommendationSchema,
    jsonInit('PATCH', { userPriorityTier }),
  );
}

export function setRecommendationAssignee(
  envId: string,
  id: string,
  assignedToId: string | null,
): Promise<Recommendation> {
  return authJson(
    `${graphBase(envId)}/recommendations/${id}/assignee`,
    recommendationSchema,
    jsonInit('PATCH', { assignedToId }),
  );
}

export function bulkRecommendations(
  envId: string,
  input: BulkRecommendationInput,
): Promise<BulkRecommendationResult> {
  return authJson(
    `${graphBase(envId)}/recommendations/bulk`,
    bulkRecommendationResultSchema,
    jsonInit('POST', input),
  );
}

export function getRecommendationComments(
  envId: string,
  id: string,
): Promise<RecommendationComments> {
  return authJson(
    `${graphBase(envId)}/recommendations/${id}/comments`,
    recommendationCommentsSchema,
  );
}
export function addRecommendationComment(
  envId: string,
  id: string,
  body: string,
): Promise<RecommendationComment> {
  return authJson(
    `${graphBase(envId)}/recommendations/${id}/comments`,
    recommendationCommentSchema,
    jsonInit('POST', { body }),
  );
}

export function guideRecommendation(
  envId: string,
  id: string,
  alternativeIntent?: string,
): Promise<RecommendationGuidance> {
  return authJson(
    `${graphBase(envId)}/recommendations/${id}/guide`,
    recommendationGuidanceSchema,
    jsonInit('POST', alternativeIntent ? { alternativeIntent } : {}),
  );
}

/** The resource's operational metrics (avg + peak) for the Relevant Metrics
 * section — loaded on demand when a recommendation is opened. */
export function getRecommendationMetrics(
  envId: string,
  id: string,
): Promise<RecommendationMetrics> {
  return authJson(`${graphBase(envId)}/recommendations/${id}/metrics`, recommendationMetricsSchema);
}

/** Mark a recommendation as opened by the current user (clears its "New" badge).
 * Fire-and-forget — returns 204 with no body, and failures are swallowed since a
 * missed view-mark only means a card stays "New" a little longer. */
export async function markRecommendationViewed(envId: string, id: string): Promise<void> {
  await authFetch(`${graphBase(envId)}/recommendations/${id}/view`, { method: 'POST' });
}
export function getDomainHistory(envId: string): Promise<DomainHistory> {
  return authJson(`${graphBase(envId)}/history`, domainHistorySchema);
}
export function getCostTrends(envId: string, days = 30): Promise<CostTrends> {
  return authJson(`${graphBase(envId)}/cost-trends?days=${days}`, costTrendsSchema);
}
export function getResourceHistory(envId: string, nodeId: string): Promise<ResourceHistory> {
  return authJson(`${graphBase(envId)}/nodes/${nodeId}/history`, resourceHistorySchema);
}

// --- Utilization assessment (Phase 5) ---
export function getDomainAssessment(envId: string): Promise<DomainAssessment> {
  return authJson(`/domains/${envId}/assessment`, domainAssessmentSchema);
}
export function listDomainAssessments(): Promise<DomainAssessment[]> {
  return authJson('/domain-assessments', z.array(domainAssessmentSchema));
}
export function getOrgSavingsSummary(): Promise<OrgSavingsSummary> {
  return authJson('/recommendations/savings-summary', orgSavingsSummarySchema);
}
export function getDashboardAssessment(): Promise<OrgAssessment> {
  return authJson('/dashboard/assessment', orgAssessmentSchema);
}
export function submitFeedback(input: CreateFeedbackInput): Promise<FeedbackReceipt> {
  return authJson('/feedback', feedbackReceiptSchema, jsonInit('POST', input));
}
export function getRecommendationActivity(
  days: number | null,
  domainId?: string,
): Promise<RecommendationActivity> {
  const params = new URLSearchParams();
  if (days != null) params.set('days', String(days));
  if (domainId) params.set('domainId', domainId);
  const qs = params.toString();
  return authJson(
    `/dashboard/recommendation-activity${qs ? `?${qs}` : ''}`,
    recommendationActivitySchema,
  );
}

// --- Resource intelligence (Phase 5): on-demand AI explanations ---
export function getAiStatus(envId: string): Promise<{ enabled: boolean }> {
  return authJson(`${graphBase(envId)}/ai-status`, z.object({ enabled: z.boolean() }));
}
export function getResourceExplanation(envId: string, nodeId: string): Promise<CachedExplanation> {
  return authJson(`${graphBase(envId)}/nodes/${nodeId}/explanation`, cachedExplanationSchema);
}
export function explainResource(envId: string, nodeId: string): Promise<ResourceExplanation> {
  return authJson(
    `${graphBase(envId)}/nodes/${nodeId}/explanation`,
    resourceExplanationSchema,
    jsonInit('POST'),
  );
}
export function syncDomain(envId: string): Promise<DomainSyncResult> {
  return authJson(`/domains/${envId}/integrations/sync`, domainSyncResultSchema, jsonInit('POST'));
}
export function getSyncStatus(envId: string): Promise<DomainSyncStatus> {
  return authJson(`/domains/${envId}/integrations/sync-status`, domainSyncStatusSchema);
}

/** Trigger an on-demand validation run for the org (non-prod only, admin). */
export function runValidation(): Promise<{ triggered: number }> {
  return authJson('/validation/run', z.object({ triggered: z.number() }), jsonInit('POST'));
}

/** Ground validated realized savings against actual billed spend (TD-011/TD-070). */
export function getSavingsReconciliation(): Promise<SavingsReconciliation> {
  return authJson('/dashboard/savings-reconciliation', savingsReconciliationSchema);
}

// --- Activity: domain health ---
/** Rolled-up sync health for every accessible domain (Activity page). */
export function getDomainHealth(): Promise<DomainHealth[]> {
  return authJson('/domains/health', z.array(domainHealthSchema));
}

// --- Notifications ---
export function getNotifications(): Promise<NotificationList> {
  return authJson('/notifications', notificationListSchema);
}
export async function markNotificationRead(id: string): Promise<void> {
  const res = await authFetch(`/notifications/${id}/read`, { method: 'POST' });
  if (!res.ok) throw await toError(res);
}
export async function markAllNotificationsRead(): Promise<void> {
  const res = await authFetch('/notifications/read-all', { method: 'POST' });
  if (!res.ok) throw await toError(res);
}
export async function dismissNotification(id: string): Promise<void> {
  const res = await authFetch(`/notifications/${id}`, { method: 'DELETE' });
  if (!res.ok) throw await toError(res);
}

// --- Organization ---
export function getOrganization(): Promise<Organization> {
  return authJson('/organization', organizationSchema);
}
export function updateOrganization(input: UpdateOrganizationInput): Promise<Organization> {
  return authJson('/organization', organizationSchema, jsonInit('PATCH', input));
}

/** Feature flags enabled for the current organization (gates UI via useFeature). */
export function getFeatures(): Promise<EnabledFeatures> {
  return authJson('/features', enabledFeaturesSchema);
}

/** Irreversibly delete the account (Danger Zone). `confirmName` must match the org
 * name. On success the account is deactivated/anonymized and the session is dead. */
/** GDPR data export (Art. 15/20) — the full personal-data bundle as JSON. */
export async function exportOrganizationData(): Promise<unknown> {
  const res = await authFetch('/organization/export');
  if (!res.ok) throw await toError(res);
  return res.json();
}

/** GDPR self-service export (Art. 15/20) — the signed-in user's OWN personal data. */
export async function exportMyData(): Promise<unknown> {
  const res = await authFetch('/me/export');
  if (!res.ok) throw await toError(res);
  return res.json();
}

export function deleteAccount(confirmName: string): Promise<{ deleted: true }> {
  return authJson(
    '/organization/delete-account',
    z.object({ deleted: z.literal(true) }),
    jsonInit('POST', { confirmName }),
  );
}
/** Set the org's MFA enforcement policy (admin-only, Feature 25 Phase B). */
export function setMfaPolicy(mfaPolicy: MfaPolicy): Promise<Organization> {
  return authJson(
    '/organization/security/mfa-policy',
    organizationSchema,
    jsonInit('PATCH', { mfaPolicy }),
  );
}

/** Update opt-in privacy controls (admin-only, data minimization). Returns the new
 * org + a purge summary when turning source ingestion off removed stored data. */
export function setPrivacySettings(
  input: UpdatePrivacySettingsInput,
): Promise<UpdatePrivacySettingsResult> {
  return authJson(
    '/organization/privacy',
    updatePrivacySettingsResultSchema,
    jsonInit('PATCH', input),
  );
}

/** Arm the TERMINAL resource-name tokenization lever (admin-only, irreversible).
 * `confirm` must match the organization name. Returns the new org + re-hash counts. */
export function enableNameTokenization(confirm: string): Promise<EnableNameTokenizationResult> {
  return authJson(
    '/organization/privacy/tokenize-names',
    enableNameTokenizationResultSchema,
    jsonInit('POST', { confirm }),
  );
}

// --- Platform status (shown on the dashboard) ---
const readinessSchema = z.object({
  status: z.string(),
  info: z.record(z.object({ status: z.string() })).optional(),
});

export interface Readiness {
  status: string;
  info: Record<string, { status: string }>;
}

export async function getVersion(): Promise<VersionInfo> {
  const res = await authFetch('/version');
  if (!res.ok) throw await toError(res);
  return versionInfoSchema.parse(await res.json());
}

export async function getReadiness(): Promise<Readiness> {
  const res = await authFetch('/health/ready');
  if (!res.ok) throw await toError(res);
  const parsed = readinessSchema.parse(await res.json());
  return { status: parsed.status, info: parsed.info ?? {} };
}
